import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChartBarIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  CircleStackIcon,
  ClipboardDocumentListIcon,
  Cog8ToothIcon,
  HomeIcon,
  UserGroupIcon,
  UserPlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const SIDEBAR_STORAGE_KEY = 'hiringcrm:sidebar-collapsed';

const Layout = ({ children }) => {
  const { user, logout, hasPermission, exitImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [openMobileMenu, setOpenMobileMenu] = useState(null);
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    const savedState = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    setIsSidebarCollapsed(savedState === 'true');
  }, []);

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
    setOpenMobileMenu(null);
    setOpenDesktopMenu(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!candidateSearch.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return undefined;
    }

    let isActive = true;
    const timeoutId = window.setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await axios.get('/api/applicants', {
          params: {
            search: candidateSearch.trim(),
            limit: 8,
            page: 1,
            sortBy: 'lastActivityAt',
            sortOrder: 'DESC'
          }
        });

        if (isActive) {
          setSearchResults(response.data.applicants || []);
          setIsSearchOpen(true);
        }
      } catch (error) {
        if (isActive) {
          setSearchResults([]);
        }
        console.error('Error searching applicants:', error);
      } finally {
        if (isActive) {
          setIsSearching(false);
        }
      }
    }, 200);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [candidateSearch]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCandidateSelect = (applicantId) => {
    setCandidateSearch('');
    setSearchResults([]);
    setIsSearchOpen(false);
    navigate(`/applicants/${applicantId}`);
  };

  const handleExitImpersonation = async () => {
    const result = await exitImpersonation();
    if (!result.success) {
      window.alert(result.message);
    }
  };

  const settingsMenu = [
    { name: 'Settings', href: '/settings', icon: Cog8ToothIcon },
    ...(user?.isSuperAdmin ? [{ name: 'Email Settings', href: '/settings/email', icon: EnvelopeIcon }] : []),
    ...(user?.isSuperAdmin ? [{ name: 'Configure Telegram Bot', href: '/settings/telegram-bot', icon: ChatBubbleOvalLeftEllipsisIcon }] : []),
    ...(user?.isSuperAdmin ? [{ name: 'Configure New Leads Page', href: '/settings/newleadsconfigure', icon: UserPlusIcon }] : []),
    ...(user?.isSuperAdmin ? [{ name: 'Users', href: '/admin', icon: UserGroupIcon }] : []),
    ...(user?.isSuperAdmin ? [{ name: 'Feedback Management', href: '/feedback/manage', icon: ClipboardDocumentListIcon }] : []),
    ...(user?.isSuperAdmin ? [{ name: 'Database Schema', href: '/db-schema', icon: CircleStackIcon }] : [])
  ];

  const applicantsMenu = hasPermission('applicant', 'create')
    ? [{ name: 'Add New Applicant', href: '/applicants?action=new', icon: UserPlusIcon }]
    : [];

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Applicants List', href: '/applicants', icon: UserGroupIcon, children: applicantsMenu },
    ...(hasPermission('new_leads_page', 'read') ? [{ name: 'Fresh Leads', href: '/new-leads', icon: UserPlusIcon }] : []),
    ...(hasPermission('applicant', 'read') ? [{ name: 'Reports', href: '/reports', icon: ChartBarIcon }] : []),
    { name: 'Team', href: '/team', icon: UserGroupIcon },
    { name: 'Jobs', href: '/jobs', icon: BriefcaseIcon },
    ...(user?.isSuperAdmin ? [{ name: 'Activity Log', href: '/activity', icon: ClipboardDocumentListIcon }] : []),
    ...(user?.isSuperAdmin ? [{ name: 'Email Templates', href: '/email-templates', icon: EnvelopeIcon }] : [])
  ];

  const bottomNavigation = [
    { name: 'Settings', icon: Cog8ToothIcon, children: settingsMenu }
  ];

  const isDesktopExpanded = !isSidebarCollapsed;

  const getItemActiveState = (item) => {
    if (item.href) {
      if (item.href === '/') {
        return location.pathname === '/';
      }

      return location.pathname === item.href;
    }

    return item.children?.some((menuItem) => location.pathname === menuItem.href) || false;
  };

  const renderDesktopNavItem = (item) => {
    const isActive = getItemActiveState(item);
    const hasChildren = Boolean(item.children?.length);
    const isOpen = openDesktopMenu === item.name;
    const isDisabled = Boolean(item.disabled);

    return (
      <div key={item.name} className="relative">
        {isDisabled ? (
          <div
            className={`flex items-center rounded-xl px-3 py-3 text-sm font-medium text-gray-400 ${
              isDesktopExpanded ? 'justify-between gap-3' : 'justify-center'
            }`}
            title={!isDesktopExpanded ? item.name : undefined}
            aria-disabled="true"
          >
            <span className={`flex items-center ${isDesktopExpanded ? 'gap-3' : ''}`}>
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {isDesktopExpanded && <span>{item.name}</span>}
            </span>
          </div>
        ) : hasChildren ? (
          <button
            type="button"
            onClick={() => setOpenDesktopMenu(isOpen ? null : item.name)}
            className={`flex items-center rounded-xl px-3 py-3 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            } ${isDesktopExpanded ? 'justify-between gap-3' : 'justify-center'}`}
            title={!isDesktopExpanded ? item.name : undefined}
          >
            <span className={`flex items-center ${isDesktopExpanded ? 'gap-3' : ''}`}>
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {isDesktopExpanded && <span>{item.name}</span>}
            </span>
            {hasChildren && isDesktopExpanded && (
              <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            )}
          </button>
        ) : (
          <Link
            to={item.href}
            className={`flex items-center rounded-xl px-3 py-3 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            } ${isDesktopExpanded ? 'justify-between gap-3' : 'justify-center'}`}
            title={!isDesktopExpanded ? item.name : undefined}
          >
            <span className={`flex items-center ${isDesktopExpanded ? 'gap-3' : ''}`}>
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {isDesktopExpanded && <span>{item.name}</span>}
            </span>
          </Link>
        )}

        {hasChildren && isOpen && isDesktopExpanded && (
          <div className="mt-2 space-y-1 rounded-xl border border-gray-200 bg-gray-50 p-2">
            {item.href && (
              <Link
                to={item.href}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-gray-900"
              >
                <item.icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span>Open {item.name}</span>
              </Link>
            )}
            {item.children.map((menuItem) => (
              <Link
                key={menuItem.name}
                to={menuItem.href}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-gray-900"
              >
                <menuItem.icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span>{menuItem.name}</span>
              </Link>
            ))}
          </div>
        )}

        {hasChildren && !isDesktopExpanded && !isDisabled && (
          <button
            type="button"
            onClick={() => setOpenDesktopMenu(isOpen ? null : item.name)}
            className="absolute inset-0 z-10 rounded-xl"
            aria-label={`Toggle ${item.name} menu`}
          >
          </button>
        )}

        {hasChildren && !isDesktopExpanded && isOpen && (
          <div
            className="absolute left-full top-0 z-30 ml-4"
          >
            <div className="min-w-[220px] rounded-xl border border-gray-200 bg-white py-2 shadow-lg">
              {item.href && (
                <Link
                  to={item.href}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  <item.icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span>Open {item.name}</span>
                </Link>
              )}
              {item.children.map((menuItem) => (
                <Link
                  key={menuItem.name}
                  to={menuItem.href}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  <menuItem.icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span>{menuItem.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMobileNavItem = (item) => {
    const isActive = getItemActiveState(item);
    const hasChildren = Boolean(item.children?.length);
    const isOpen = openMobileMenu === item.name;
    const isDisabled = Boolean(item.disabled);

    if (isDisabled) {
      return (
        <div
          key={item.name}
          className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-gray-400"
          aria-disabled="true"
        >
          <item.icon className="h-5 w-5 flex-shrink-0" />
          <span>{item.name}</span>
        </div>
      );
    }

    if (hasChildren) {
      return (
        <div key={item.name} className="rounded-xl">
          <button
            type="button"
            onClick={() => setOpenMobileMenu(isOpen ? null : item.name)}
            className={`flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-3">
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span>{item.name}</span>
            </span>
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {isOpen && (
            <div className="mt-2 space-y-1 rounded-xl border border-gray-200 bg-gray-50 p-2">
              {item.href && (
                <Link
                  to={item.href}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-gray-900"
                >
                  <item.icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span>Open {item.name}</span>
                </Link>
              )}
              {item.children.map((menuItem) => (
                <Link
                  key={menuItem.name}
                  to={menuItem.href}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-gray-900"
                >
                  <menuItem.icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span>{menuItem.name}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.name}
        to={item.href}
        className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-blue-50 text-blue-700'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <item.icon className="h-5 w-5 flex-shrink-0" />
        <span>{item.name}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200 bg-white shadow-sm transition-all duration-200 ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } hidden w-72 lg:flex lg:translate-x-0 ${isDesktopExpanded ? 'lg:w-72' : 'lg:w-20'}`}
      >
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4">
          <Link
            to="/"
            className={`flex items-center overflow-hidden ${isDesktopExpanded ? 'gap-3' : 'justify-center lg:w-full'}`}
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 text-sm font-bold text-white">
              HC
            </div>
            {isDesktopExpanded && (
              <div className="min-w-0">
                <div className="truncate text-base font-semibold text-gray-900">Hiring CRM</div>
                <div className="truncate text-xs text-gray-500">Recruiting workspace</div>
              </div>
            )}
          </Link>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed((prev) => !prev)}
              className="hidden rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 lg:inline-flex"
              aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setIsMobileSidebarOpen(false)}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 lg:hidden"
              aria-label="Close sidebar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <nav className="space-y-2">
            {navigation.map(renderDesktopNavItem)}
          </nav>
        </div>

        <div className="border-t border-gray-200 p-4">
          <nav className="space-y-2">
            {bottomNavigation.map(renderDesktopNavItem)}
          </nav>
        </div>
      </aside>

      <div className={`${isDesktopExpanded ? 'lg:pl-72' : 'lg:pl-20'}`}>
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur">
          {user?.impersonation?.active && (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 sm:px-6 lg:px-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm">
                  Viewing the app as <span className="font-semibold">{user.name || user.email}</span>.
                  {' '}Signed in originally as{' '}
                  <span className="font-semibold">
                    {user.impersonation.impersonator?.name || user.impersonation.impersonator?.email || 'super admin'}
                  </span>.
                </div>
                <button
                  type="button"
                  onClick={handleExitImpersonation}
                  className="inline-flex items-center justify-center rounded-xl bg-amber-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-950"
                >
                  Exit user view
                </button>
              </div>
            </div>
          )}
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(true)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 lg:hidden"
                aria-label="Open sidebar"
              >
                <Bars3Icon className="h-5 w-5" />
              </button>
              <div ref={searchContainerRef} className="relative w-full max-w-xl">
                <label htmlFor="candidate-search" className="sr-only">
                  Search candidates by name or number
                </label>
                <div className="flex items-center rounded-2xl border border-gray-200 bg-gray-50 px-3 shadow-sm transition focus-within:border-blue-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-100">
                  <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                  <input
                    id="candidate-search"
                    type="text"
                    value={candidateSearch}
                    onChange={(event) => setCandidateSearch(event.target.value)}
                    onFocus={() => {
                      if (candidateSearch.trim()) {
                        setIsSearchOpen(true);
                      }
                    }}
                    placeholder="Search candidate by name or number"
                    className="w-full bg-transparent px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  />
                </div>

                {isSearchOpen && candidateSearch.trim() && (
                  <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-40 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                    {isSearching ? (
                      <div className="px-4 py-3 text-sm text-gray-500">Searching candidates...</div>
                    ) : searchResults.length > 0 ? (
                      <div className="max-h-80 overflow-y-auto py-2">
                        {searchResults.map((applicant) => (
                          <button
                            key={applicant.id}
                            type="button"
                            onClick={() => handleCandidateSelect(applicant.id)}
                            className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-gray-50"
                          >
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-gray-900">{applicant.name}</div>
                              <div className="truncate text-xs text-gray-500">
                                {[applicant.countryCode, applicant.phone].filter(Boolean).join(' ') || applicant.email || 'No contact details'}
                              </div>
                            </div>
                            <div className="flex-shrink-0 text-xs font-medium text-blue-600">
                              #{applicant.id}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">No candidates found.</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="ml-4 flex flex-shrink-0 items-center gap-2 sm:gap-3">
              <Link
                to="/feedback"
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
              >
                <ChatBubbleLeftRightIcon className="h-5 w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Feedback</span>
              </Link>

              <div className="hidden text-right sm:block">
                <div className="max-w-[180px] truncate text-sm font-medium text-gray-900">
                  {user?.email || 'Signed in'}
                </div>
                <div className="text-xs text-gray-500">
                  {user?.isSuperAdmin ? 'Super Admin' : 'Team member'}
                </div>
              </div>

              <div
                className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700 sm:hidden"
                title={user?.email || 'Signed in'}
              >
                {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        <main className="py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
      <div className="lg:hidden">
        <aside
          className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-gray-200 bg-white shadow-sm transition-transform duration-200 ${
            isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-sm font-bold text-white">
                HC
              </div>
              <div>
                <div className="text-base font-semibold text-gray-900">Hiring CRM</div>
                <div className="text-xs text-gray-500">Recruiting workspace</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsMobileSidebarOpen(false)}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close sidebar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <nav className="space-y-2">
              {navigation.map(renderMobileNavItem)}
            </nav>
          </div>

          <div className="border-t border-gray-200 p-4">
            <nav className="space-y-2">
              {bottomNavigation.map(renderMobileNavItem)}
            </nav>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Layout;
